import { MotionConfig, MotionGlobalConfig } from "motion/react";
import { useEffect, type ReactNode } from "react";
import { styleNonceProps } from "./styleNonce.ts";
import { useTheme } from "./ThemeProvider.tsx";

/**
 * `contracts/design-system.md` § Motion, T292 (2026-09-21): the one place `motion/react` is
 * reached from outside a component that animates with it. The token stylesheet cannot reach the
 * components taken from Spectrum UI, which animate through Motion, so the resolved reduced-motion
 * setting is carried to them here: `MotionConfig` tells them to take their reduced paths (no
 * confetti, no stretch, instant moves), and `MotionGlobalConfig.skipAnimations` finishes every
 * Motion animation at once.
 *
 * It is a module of its own rather than part of `ThemeProvider` because the public entry mounts
 * the provider and no Motion component: while the provider imported the engine, the public chunk
 * carried its 41 KB gzipped and stood 32.5 KB over the 135 KB of FR-015-120. The authenticated
 * entry wraps its tree in this, and so does the shared test harness
 * (`packages/test-utils/src/render.tsx`), so every surface that draws a Motion component has it.
 *
 * It also carries the document's style nonce (`styleNonce.ts`, FR-015-151): `AnimatePresence` in
 * `popLayout` mode, which the toast region uses, writes a `<style>` element to hold an exiting
 * child in place, and takes its nonce from here.
 */
export function MotionBridge({ children }: { readonly children: ReactNode }) {
  const { motionReduced } = useTheme();
  useEffect(() => {
    MotionGlobalConfig.skipAnimations = motionReduced;
  }, [motionReduced]);
  return (
    <MotionConfig reducedMotion={motionReduced ? "always" : "never"} {...styleNonceProps()}>
      {children}
    </MotionConfig>
  );
}
